import { LogOut } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const PASSWORD_STR_COLOR = {
    Weak: 'text-red-500',
    Moderate: 'text-yellow-500',
    Strong: 'text-green-500'
};
const PASSWORD_MATCH_COLOR = {
    match: 'text-green-500',
    mismatch: 'text-red-500',
    empty: 'text-gray-500'
};

const Dashboard = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const evaluatePasswordStrength = (password) => {
        const criteria = [
            { regex: /.{8,}/, message: 'At least 8 characters' },
            { regex: /[A-Z]/, message: 'An uppercase letter' },
            { regex: /[a-z]/, message: 'A lowercase letter' },
            { regex: /[0-9]/, message: 'A number' },
            { regex: /[^A-Za-z0-9]/, message: 'A special character' },
        ];

        const passedCriteria = criteria.filter((criterion) => criterion.regex.test(password));
        const strength = passedCriteria.length;

        if (strength <= 2) return 'Weak';
        if (strength === 3 || strength === 4) return 'Moderate';
        return 'Strong';
    };

    const handlePasswordChange = (password) => {
        setNewPassword(password);
        const strength = evaluatePasswordStrength(password);
        setPasswordStrength(strength);
    };

    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
    const matchStatus = !confirmPassword
        ? "empty"
        : (passwordsMatch ? "match" : "mismatch");

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            Swal.fire({
                title: 'Error',
                text: 'New password and confirm password do not match',
                icon: 'error',
                width: 370,
            });
            return;
        }

        const criteriaRegex = /.{8,}/.test(newPassword) && /[^A-Za-z0-9]/.test(newPassword);
        if (!criteriaRegex) {
            Swal.fire({
                title: 'Error',
                text: 'Password must be at least 8 characters and include a special character',
                icon: 'error',
                width: 370,
            });
            return;
        }

        try {
            setIsLoading(true);
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                throw new Error('Unauthorized: No access token found');
            }

            const response = await fetch('https://portal.lsb.go.ke/api/change_password/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ password: newPassword, re_password: confirmPassword }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData.message || 'Failed to change password';
                throw new Error(errorMessage);
            }

            Swal.fire({
                title: 'Success',
                text: 'Password changed successfully',
                icon: 'success',
                width: 370,
            });
            navigate('/portal');
        } catch (error) {
            console.error('Error changing password:', error);
            Swal.fire({
                title: 'Error',
                text: `An error occurred: ${error.message}`,
                icon: 'error',
                width: 370,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('accessToken');
        Swal.fire({
            title: 'Success',
            text: 'You have been logged out.',
            icon: 'success',
            width: 370,
        });
        navigate('/portal');
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-10">
            <div className="w-full max-w-md p-8 rounded-xl shadow-2xl bg-white border-2 border-[#EBCAB3]">
                <div className="flex justify-end mb-6">
                    <button
                        onClick={handleLogout}
                        className="py-1 px-3 bg-red-600 hover:bg-red-700 transition uppercase text-white flex gap-2 items-center rounded-md text-sm"
                    >
                        Logout <LogOut className='w-5 h-5' />
                    </button>
                </div>

                <h2 className="text-center font-extrabold uppercase mb-8 underline underline-offset-4 tracking-wider text-2xl text-[#3d0707]">
                    Change Password
                </h2>

                <div className="mb-6 px-4 py-3 text-primary-800 font-medium">
                    <p><b className="underline underline-offset-2">Note:</b> To ensure your account is secure, your new password should have at least:</p>

                    <div className="mt-1 ml-4 flex flex-col">
                        <span className='font-bold'>* 8 characters long</span>
                        <span className='font-bold'>* One uppercase letter (A-Z)</span>
                        <span className='font-bold'>* One lowercase letter (a-z)</span>
                        <span className='font-bold'>* One number (0-9)</span>
                        <span className='font-bold'>* One special character (e.g., ! @ # $ % ^ & *)</span>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-7 flex flex-col items-center">
                    <div className="w-full">
                        <label
                            htmlFor="password"
                            className="block mb-1 font-semibold text-primary-800"
                        >
                            New Password
                        </label>

                        <div className="relative">
                            <input
                                placeholder='Enter new password'
                                id="password"
                                type={showNewPassword ? 'text' : 'password'}
                                value={newPassword}
                                autoComplete="new-password"
                                onChange={(e) => handlePasswordChange(e.target.value)}
                                className="w-full rounded-lg border bg-gray-100 py-3 pl-4 pr-10 text-sm font-semibold transition focus:outline-none focus:ring-0 focus:ring-[#3d0707]"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#3d0707] text-lg focus:outline-none"
                                tabIndex={-1}
                                aria-label={showNewPassword ? "Hide password" : "Show password"}
                            >
                                <i className={`fa-regular ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                            </button>
                        </div>
                        {newPassword && (
                            <div className={`mt-2 text-sm font-medium transition ${PASSWORD_STR_COLOR[passwordStrength]}`}>
                                Strength: {passwordStrength}
                            </div>
                        )}
                    </div>

                    <div className="w-full">
                        <label
                            htmlFor="confirmPassword"
                            className="block mb-1 font-semibold text-primary-800"
                        >
                            Confirm Password
                        </label>
                        <div className="relative">
                            <input
                                placeholder='Re-enter new password'
                                id="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                autoComplete="new-password"
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full rounded-lg border bg-gray-100 py-3 pl-4 pr-10 text-sm font-semibold transition focus:outline-none focus:ring-0 focus:ring-[#3d0707]"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#3d0707] text-lg focus:outline-none"
                                tabIndex={-1}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                <i className={`fa-regular ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                            </button>
                        </div>
                        {confirmPassword && (
                            <div className={`mt-2 text-sm font-medium transition ${PASSWORD_MATCH_COLOR[matchStatus]}`}>
                                {passwordsMatch
                                    ? "Passwords Match"
                                    : "Passwords do not Match"}
                            </div>
                        )}
                    </div>

                    <div className="w-full flex justify-center pt-1">
                        <button
                            type="submit"
                            className="w-full font-bold py-3 px-8 rounded bg-[#3d0707] text-white uppercase tracking-wider text-lg transition hover:font-medium flex items-center justify-center"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                "Submit"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Dashboard;